import { useEffect, useRef } from 'react';
import { Box, Divider, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { v4 as uuidv4 } from 'uuid';

import { Product } from '@setvi/shared/interfaces';
import { AIIcon, ErrorIcon } from '@setvi/shared/components/sicons';
import {
  SRoundedContainer,
  SText,
  SEmptyState,
  SButton
} from '@setvi/shared/components';

import { AIloader } from 'componentsV2/ai';
import { AILoader } from 'pages/products/components';
import { aiProductChatMessages } from 'pages/products/components/ai/loader/messages';
import { AITextarea } from 'componentsV2/ai/modules/ask-product/tabs/chat/components';
import { CustomerPreferencesInterface } from 'componentsV2/customer-preferences/interfaces';

import { useProductAssistant } from './hook';
import { ContentTitleContainer } from '../product-info';
import { Container } from '../sales-enablement/styled';
import { SalesKitFeedback } from '../sales-enablement/components';
import {
  AIBox,
  ChatBox,
  ChatContainer,
  ChatContent,
  IconBox,
  Message,
  Placeholder,
  Question,
  QuestionsContainer,
  QuestionsTitle,
  QuestionsWrapper,
  TextareaBox,
  UserBox
} from './styled';

export const ProductAssistant = ({
  product,
  customerPreferences
}: {
  product: Product;
  customerPreferences?: CustomerPreferencesInterface;
}) => {
  const {
    error,
    loading,
    initLoading,
    questions,
    conversation,
    questionsLoading,
    onSubmit,
    refreshQuestions
  } = useProductAssistant({ product, customerPreferences });

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [conversation]);

  if (initLoading)
    return (
      <Container>
        <AILoader messages={aiProductChatMessages} />
      </Container>
    );

  if (error?.status === 529) {
    return (
      <Container>
        <SEmptyState
          svg={
            <Box
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#FFF5F5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16
              }}>
              <ErrorIcon
                style={{
                  width: 40,
                  height: 40,
                  color: '#FC5A5A'
                }}
              />
            </Box>
          }
          title="Oops! Something went wrong"
          subTitle={
            error.message ||
            "We're having trouble loading the AI assistant. Please try again."
          }
        />
      </Container>
    );
  }

  return (
    <Container>
      <ContentTitleContainer
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <SText title="Ask AI" weight="semibold" />
        <SalesKitFeedback
          product={product}
          label="How can we improve AI Product Assistant?"
        />
      </ContentTitleContainer>
      <Box display="flex" overflow="hidden" flex={1} pb={2} gridGap="16px">
        <SRoundedContainer style={{ width: '75%' }}>
          <ChatContainer>
            {conversation?.length ? (
              <ChatContent
                // @ts-ignore
                ref={contentRef}>
                {conversation?.map(({ question, answer }) => (
                  <ChatBox key={uuidv4()}>
                    <UserBox>
                      <Message>{question}</Message>
                    </UserBox>

                    <AIBox
                      style={{
                        alignItems:
                          loading && !answer?.trim() ? 'center' : 'flex-start'
                      }}>
                      <IconBox>
                        <AIIcon fontSize="small" />
                      </IconBox>
                      {loading && !answer?.trim() ? (
                        <AIloader />
                      ) : (
                        <Message
                          dangerouslySetInnerHTML={{ __html: answer }}
                          style={{
                            background: '#F0F0F4'
                          }}
                        />
                      )}
                    </AIBox>
                  </ChatBox>
                ))}
              </ChatContent>
            ) : (
              <Placeholder>
                <Typography
                  variant="h6"
                  style={{ marginBottom: 16, fontWeight: 600 }}>
                  Welcome to Ask AI!
                </Typography>
                <Typography
                  variant="body1"
                  style={{
                    marginBottom: 24,
                    lineHeight: 1.5,
                    color: '#666666'
                  }}>
                  Ask me anything about this product and I'll help you find the
                  information you need.
                </Typography>
                <Typography
                  variant="body2"
                  style={{ lineHeight: 1.4, color: '#888888' }}>
                  You can start by clicking on one of the frequently asked
                  questions on the right, or type your own question.
                </Typography>
              </Placeholder>
            )}

            <TextareaBox>
              <AITextarea width="90%" onSubmit={onSubmit} disabled={loading} />
            </TextareaBox>
          </ChatContainer>
        </SRoundedContainer>

        <QuestionsContainer>
          <QuestionsTitle>
            <SText title="Suggested Questions" weight="semibold" />
            <SButton
              variant="text"
              onClick={refreshQuestions}
              disabled={questionsLoading}
              loading={questionsLoading}>
              Refresh
            </SButton>
          </QuestionsTitle>
          <Divider />
          <QuestionsWrapper>
            {questionsLoading
              ? Array.from({ length: 10 }, v => (
                  <Skeleton
                    key={v?.toString()}
                    variant="rect"
                    width="100%"
                    style={{ borderRadius: 12 }}
                    height={35}
                  />
                ))
              : questions?.map(question => (
                  <Question
                    fullWidth
                    color="default"
                    key={question}
                    variant="text"
                    disabled={loading}
                    onClick={() => onSubmit(question)}>
                    {question}
                  </Question>
                ))}
          </QuestionsWrapper>
        </QuestionsContainer>
      </Box>
    </Container>
  );
};
